import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { ExpandMore } from "@material-ui/icons";
import React from "react";

import { useBasicWtpDataAdjustmentsInfo } from "./useBasicWtpDataAdjustmentsInfo";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { useSubmitData } from "../../../hooks/useSubmitData";
import Loader from "../../../components/Loader";
import { getButtonText, getButtonIcon } from "./utils";

function BasicWtpDataAdjustmentInfo({
  selectedDataAdjustment,
  setSelectedDataAdjustment,
  refetch,
}) {
  const {
    isLoading,
    error,
    handleUpdateState,
    state,
    lookups,
    isDirty,
    handleResetState,
  } = useBasicWtpDataAdjustmentsInfo(selectedDataAdjustment);

  const { handleSubmit, isFormSubmitting } = useSubmitData(
    selectedDataAdjustment,
    setSelectedDataAdjustment,
    state,
    "data-overrides-wtp",
    "ndx",
    refetch
  );

  if (error) return "An error has occurred: " + error.message;
  return (
    <>
      {isLoading ? (
        <Paper style={{ width: "100%", margin: "8px 0" }}>
          <Loader />
        </Paper>
      ) : (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box style={{ width: "100%" }}>
              <Typography variant="h3">General Information</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid
                item
                xs={12}
                md={6}
                xl={4}
                style={{
                  position: "relative",
                }}
              >
                <FormControl
                  component="fieldset"
                  style={{ marginRight: "10px", width: "100%" }}
                >
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <DatePicker
                      error={!state?.collect_date}
                      required
                      value={state?.collect_date}
                      onChange={(date) =>
                        handleUpdateState("collect_date", date)
                      }
                      id="created-on"
                      variant="inline"
                      format="LLLL do, yyyy"
                      autoOk
                      inputVariant="outlined"
                      label="Collection Date"
                    />
                  </MuiPickersUtilsProvider>
                  {!state?.collect_date && (
                    <FormHelperText error>*Required field</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                xl={4}
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <FormControl
                  variant="outlined"
                  style={{ width: "100%" }}
                  error={!state.location_ndx}
                  required
                >
                  {!lookups?.locations.length > 0 ? (
                    <Loader />
                  ) : (
                    <>
                      <InputLabel id="location_ndx">Location</InputLabel>
                      <Select
                        labelId="location_ndx"
                        id="location_ndx"
                        label="Location"
                        name="precinct_num"
                        value={state.location_ndx ?? ""}
                        onChange={(e) => {
                          handleUpdateState("location_ndx", e.target.value);
                        }}
                      >
                        {lookups?.locations.map((option) => (
                          <MenuItem
                            key={option.location_ndx}
                            value={option.location_ndx}
                          >
                            {option.location_desc}
                          </MenuItem>
                        ))}
                      </Select>
                    </>
                  )}
                  {!state.location_ndx && (
                    <FormHelperText error>*Required field</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                xl={4}
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <FormControl
                  variant="outlined"
                  style={{ width: "100%" }}
                  error={!state.measure_type_ndx}
                  required
                >
                  {!lookups?.parameters.length > 0 ? (
                    <Loader />
                  ) : (
                    <>
                      <InputLabel id="measure_type_ndx">Parameter</InputLabel>
                      <Select
                        labelId="measure_type_ndx"
                        id="measure_type_ndx"
                        label="Parameter"
                        name="precinct_num"
                        value={state.measure_type_ndx ?? ""}
                        onChange={(e) => {
                          handleUpdateState("measure_type_ndx", e.target.value);
                        }}
                      >
                        {lookups?.parameters.map((option) => (
                          <MenuItem
                            key={option.measure_type_ndx}
                            value={option.measure_type_ndx}
                          >
                            {option.parameter_desc}
                          </MenuItem>
                        ))}
                      </Select>
                    </>
                  )}
                  {!state.measure_type_ndx && (
                    <FormHelperText error>*Required field</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                xl={4}
                style={{
                  position: "relative",
                }}
              >
                <FormControl variant="outlined" style={{ width: "100%" }}>
                  <TextField
                    required
                    error={
                      state.override_value === null ||
                      state.override_value === undefined ||
                      state.override_value === ""
                    }
                    type="number"
                    variant="outlined"
                    label="Adjustment Value"
                    style={{ width: "100%" }}
                    onChange={(e) => {
                      if (e.target.value === "") {
                        handleUpdateState("override_value", null);
                      } else {
                        handleUpdateState("override_value", +e.target.value);
                      }
                    }}
                    value={state?.override_value ?? ""}
                    InputProps={{
                      inputProps: {
                        onKeyPress: (e) => {
                          if (e.key === "e") {
                            e.preventDefault();
                          }
                        },
                      },
                    }}
                  />
                  {(state.override_value === null ||
                    state.override_value === undefined ||
                    state.override_value === "") && (
                    <FormHelperText error>*Required field</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                xl={8}
                style={{
                  position: "relative",
                }}
              >
                <FormControl
                  variant="outlined"
                  style={{ width: "100%" }}
                  required
                >
                  <TextField
                    multiline
                    rows={3}
                    variant="outlined"
                    label="Notes"
                    style={{ width: "100%" }}
                    onChange={(e) => {
                      handleUpdateState("notes", e.target.value);
                    }}
                    value={state?.notes ?? ""}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      <AccordionActions>
        <Button size="small" onClick={handleResetState}>
          Reset
        </Button>
        <Button
          size="small"
          color="secondary"
          variant="contained"
          onClick={handleSubmit}
          disabled={
            !isDirty ||
            !state.collect_date ||
            !state.location_ndx ||
            !state.measure_type_ndx ||
            state.override_value === null ||
            state.override_value === undefined ||
            state.override_value === "" ||
            isFormSubmitting
          }
          startIcon={getButtonIcon(isFormSubmitting)}
          style={{ width: "100px" }}
        >
          {getButtonText(isFormSubmitting, selectedDataAdjustment === 0)}
        </Button>
      </AccordionActions>
    </>
  );
}

export default BasicWtpDataAdjustmentInfo;
